// iPhones save photos as HEIC. Safari opens them itself; other browsers can't, so for those the photo is decoded
// here with libheif (WebAssembly), loaded only the first time one is needed. This only makes the display copy and
// thumbnail: the original HEIC is still uploaded exactly as it is.

// An "ftyp" box whose brand says HEIC or HEIF, as worker/images.ts checks on the server.
const BRANDS = ['heic', 'heix', 'heim', 'heis', 'hevc', 'hevx', 'mif1', 'msf1']

export async function isHeif(file: Blob) {
  const head = new Uint8Array(await file.slice(0, 12).arrayBuffer())
  const text = String.fromCharCode(...head)
  return text.slice(4, 8) === 'ftyp' && BRANDS.includes(text.slice(8, 12))
}

type Image = {
  get_width(): number
  get_height(): number
  is_primary(): boolean
  display(target: ImageData, done: (result: ImageData | null) => void): void
  free(): void
}
type Libheif = { HeifDecoder: new () => { decode(bytes: Uint8Array): Image[] } }

let libheif: Promise<Libheif> | null = null
const load = () =>
  (libheif ??= import('libheif-js/libheif-wasm/libheif-bundle.mjs').then((m) => m.default() as Libheif))

// The photo's main image, upright (HEIF keeps its turn and flip in the file, and libheif applies them).
export async function decodeHeif(file: Blob): Promise<ImageBitmap> {
  const lib = await load()
  const images = new lib.HeifDecoder().decode(new Uint8Array(await file.arrayBuffer()))
  try {
    const image = images.find((x) => x.is_primary()) ?? images[0]
    if (!image) throw new Error('no image in this HEIF file')
    const width = image.get_width()
    const height = image.get_height()
    const pixels = await new Promise<ImageData>((resolve, reject) =>
      image.display(new ImageData(width, height), (result) =>
        result ? resolve(result) : reject(new Error('this HEIF image could not be decoded')),
      ),
    )
    return await createImageBitmap(pixels)
  } finally {
    images.forEach((x) => x.free())
  }
}
